import { useEditor, EditorContent, Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react'
import { Plus, ImageIcon, MousePointerClick, List, ListOrdered, Code } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { VariableExtension } from './VariableExtension'
import { MacroImageExtension } from './MacroImageExtension'
import { ButtonExtension } from './ButtonExtension'
import BubbleMenuToolbar from './BubbleMenuToolbar'
import VariablePicker from './VariablePicker'
import { Variable } from '@/lib/variables'

interface PostEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  minHeight?: string
  onImageUpload?: (file: File) => Promise<string>
  hideButton?: boolean
}

export interface PostEditorRef {
  editor: Editor | null
}

const PostEditor = forwardRef<PostEditorRef, PostEditorProps>(
  ({ value, onChange, placeholder = 'Escreva o conteúdo do post...', minHeight = '120px', onImageUpload, hideButton }, ref) => {
    const [showInsertMenu, setShowInsertMenu] = useState(false)
    const [showVariablePicker, setShowVariablePicker] = useState(false)
    const initialValueRef = useRef(value)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2] },
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
        VariableExtension,
        MacroImageExtension.configure({
          onUpload: onImageUpload,
        }),
        ButtonExtension,
      ],
      content: value || '',
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        onChange(html)
      },
      editorProps: {
        attributes: {
          class: 'prose prose-sm max-w-none prose-p:my-0 prose-p:leading-normal focus:outline-none',
        },
      },
    })

    useImperativeHandle(ref, () => ({ editor }))

    // Update editor content when value changes externally (only on initial load)
    useEffect(() => {
      if (editor && value !== initialValueRef.current && !editor.isFocused) {
        const currentHtml = editor.getHTML()
        if (currentHtml !== value) {
          editor.commands.setContent(value || '')
          initialValueRef.current = value
        }
      }
    }, [value, editor])

    const handleInsertVariable = useCallback(
      (variable: Variable) => {
        if (editor) {
          editor.commands.insertVariable({
            key: variable.key,
            label: variable.label,
            fallback: null,
          })
          editor.commands.focus()
        }
        setShowVariablePicker(false)
        setShowInsertMenu(false)
      },
      [editor]
    )

    const handleImageSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file && editor) {
          editor.commands.uploadImage(file)
        }
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        setShowInsertMenu(false)
      },
      [editor]
    )

    const handleInsertButton = useCallback(() => {
      if (editor) {
        editor.commands.insertButton()
        editor.commands.focus()
      }
      setShowInsertMenu(false)
    }, [editor])

    const handleToggleBulletList = useCallback(() => {
      if (editor) {
        editor.commands.toggleBulletList()
        editor.commands.focus()
      }
      setShowInsertMenu(false)
    }, [editor])

    const handleToggleOrderedList = useCallback(() => {
      if (editor) {
        editor.commands.toggleOrderedList()
        editor.commands.focus()
      }
      setShowInsertMenu(false)
    }, [editor])

    return (
      <div className="relative">
        {/* Hidden file input for image upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
          className="hidden"
          onChange={handleImageSelect}
        />

        {/* Editor Content - borderless, integrates into parent */}
        <div style={{ minHeight }}>
          <EditorContent
            editor={editor}
            className="[&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_h1]:text-xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mb-1 [&_.ProseMirror_h2]:text-lg [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mb-1 [&_.ProseMirror_code]:bg-gray-100 [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm [&_.ProseMirror_pre]:bg-gray-900 [&_.ProseMirror_pre]:text-gray-100 [&_.ProseMirror_pre]:p-3 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_a]:text-blue-600 [&_.ProseMirror_a]:underline [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-4 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-4 [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-2"
          />
        </div>

        {/* Bubble Menu */}
        {editor && (
          <BubbleMenu
            editor={editor}
            shouldShow={({ state }) => {
              const { from, to } = state.selection
              const text = state.doc.textBetween(from, to, '')
              return text.length > 0
            }}
          >
            <BubbleMenuToolbar editor={editor} />
          </BubbleMenu>
        )}

        {/* Bottom Toolbar */}
        <div className="border-t border-gray-100 px-3 py-1.5 flex items-center gap-2">
          <Popover open={showInsertMenu} onOpenChange={setShowInsertMenu}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                Inserir
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-1 w-48" side="top">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              >
                <ImageIcon className="w-4 h-4" />
                Imagem
              </button>
              {!hideButton && (
                <button
                  type="button"
                  onClick={handleInsertButton}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                >
                  <MousePointerClick className="w-4 h-4" />
                  Botão
                </button>
              )}
              <button
                type="button"
                onClick={handleToggleBulletList}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              >
                <List className="w-4 h-4" />
                Lista
              </button>
              <button
                type="button"
                onClick={handleToggleOrderedList}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              >
                <ListOrdered className="w-4 h-4" />
                Lista numerada
              </button>
              <div className="border-t border-gray-100 my-1" />
              <Popover open={showVariablePicker} onOpenChange={setShowVariablePicker}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                  >
                    <Code className="w-4 h-4" />
                    Variável
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-80" side="right">
                  <VariablePicker onSelect={handleInsertVariable} onClose={() => setShowVariablePicker(false)} />
                </PopoverContent>
              </Popover>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    )
  }
)

PostEditor.displayName = 'PostEditor'
export default PostEditor
